<template>
  <v-card flat>
    <v-card-item>
      <template v-slot:prepend>
        <v-avatar color="secondary" size="small">
          <v-icon icon="mdi-check-decagram"></v-icon>
        </v-avatar>
      </template>
      <v-card-title class="text-subtitle-1 text-primary">
        {{ $t("labels.title-allowed") }}
      </v-card-title>
      <v-card-subtitle class="text-caption">
        {{ $t("strings.allowed-hint") }}
        <v-tooltip activator="parent" location="top">
          {{ $t("strings.allowed-hint") }}
        </v-tooltip>
      </v-card-subtitle>
    </v-card-item>
    <v-card-text class="pt-2 pb-1">
      <v-row class="d-flex align-center" dense>
        <v-col cols="9">
          <v-text-field
            v-model="newItem"
            :placeholder="placeholder || $t('strings.placeholder')"
            dense
            hide-details
            @keyup.enter="onAdd"
            variant="outlined"
            single-line
            density="compact"
            :disabled="disabled"
          />
        </v-col>
        <v-col cols="auto">
          <v-btn
            class="mr-2"
            :disabled="disabled || !canAdd"
            color="secondary"
            @click="onAdd"
            rounded="xl"
            size="x-small"
            variant="outlined"
            icon="mdi-plus"
            :title="$t('strings.add-value')"
          >
          </v-btn>
          <v-btn
            :disabled="disabled || !(itemsObjects && itemsObjects.length)"
            color="secondary"
            @click="requestClearAll"
            rounded="xl"
            size="x-small"
            variant="outlined"
            icon="mdi-delete-sweep"
            :title="$t('labels.clear-all')"
          >
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text class="pt-0 pb-1" style="height: 200px; overflow: auto">
      <SortableValues
        v-if="itemsObjects !== null && itemsObjects.length > 0"
        v-model="itemsObjects"
        @reorder="onChildReorder"
        :disabled="disabled"
        @request-remove="onChildRequestRemove"
      />
    </v-card-text>
    <v-divider class="mt-4"></v-divider>
  </v-card>

  <!-- Confirm dialogs (shared) -->
  <ConfirmDialog
    v-model="showConfirmRemove"
    :message="
      t('strings.confirm-message', {
        name: pendingRemove?.item?.name || pendingRemove?.item?.title,
      })
    "
    @confirm="confirmRemove"
    @cancel="cancelRemove"
  />

  <ConfirmDialog
    v-model="showConfirmClear"
    :message="
      t('strings.values-clear-all-confirm') ||
      t('strings.confirm-message', { name: '' })
    "
    @confirm="confirmClearAll"
    @cancel="cancelClearAll"
  />
  <!--Snackbar-->
  <v-snackbar
    v-model="snackbar.show"
    :color="snackbar.color"
    :timeout="3000"
    location="top"
  >
    {{ snackbar.message }}
    <template v-slot:actions>
      <v-btn variant="text" @click="snackbar.show = false">{{
        $t("labels.close")
      }}</v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import SortableValues from "./SortableValues.vue";
import ConfirmDialog from "./ConfirmDialog.vue";

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  placeholder: { type: String, default: "" },
  maxItems: { type: Number, default: null },
  allowDuplicates: { type: Boolean, default: false },
  addLabel: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue", "reorder", "added", "removed"]);

const { t } = useI18n();

const snackbar = ref({
  show: false,
  message: "",
  color: "error",
});
const pendingRemove = ref(null);
const showConfirmRemove = ref(false);
const showConfirmClear = ref(false);

function showSnackbar(message, color = "error") {
  snackbar.value.message = message;
  snackbar.value.color = color;
  snackbar.value.show = true;
}

let uidCounter = 1;
function makeObj(name, id) {
  return { id: id ?? `s-${uidCounter++}`, name };
}

// Extract string from item (could be string or object)
function extractString(item) {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") {
    return item.name || item.title || String(item);
  }
  return String(item);
}

const itemsObjects = ref([]);
function syncObjectsFromStrings(items) {
  const existingByName = new Map(itemsObjects.value.map((o) => [o.name, o]));
  itemsObjects.value = (items || []).map((item) => {
    const str = extractString(item);
    return existingByName.get(str) || makeObj(str);
  });
}

syncObjectsFromStrings(props.modelValue);

// watch parent changes
watch(
  () => props.modelValue,
  (v) => {
    // Only sync if the names are different (avoid loops)
    const currentNames = itemsObjects.value.map((o) => o.name);
    const incomingNames = (v || []).map((item) => extractString(item));
    if (JSON.stringify(currentNames) !== JSON.stringify(incomingNames)) {
      syncObjectsFromStrings(v);
    }
  },
  { deep: true },
);

// when the internal objects change, emit strings to parent (but avoid loops)
watch(
  itemsObjects,
  (v) => {
    const strings = v.map((o) => o.name);
    if (JSON.stringify(strings) !== JSON.stringify(props.modelValue)) {
      emit("update:modelValue", strings);
    }
  },
  { deep: true },
);

function onChildReorder(arrObjs) {
  const strings = (arrObjs || []).map((o) => o.name);
  emit("reorder", strings);
  emit("update:modelValue", strings);
}

function onChildRequestRemove(payload) {
  // payload: { index, item }
  pendingRemove.value = payload;
  showConfirmRemove.value = true;
}

function confirmRemove() {
  if (!pendingRemove.value) {
    showConfirmRemove.value = false;
    return;
  }
  const payload = pendingRemove.value;
  // try to find the item by id first (safer if indexes changed)
  const id = payload.item && payload.item.id;
  let idx = -1;
  if (id) idx = itemsObjects.value.findIndex((o) => o.id === id);
  if (idx === -1) idx = payload.index ?? -1;
  if (idx >= 0 && idx < itemsObjects.value.length) {
    const removed = itemsObjects.value.splice(idx, 1)[0];
    const strings = itemsObjects.value.map((o) => o.name);
    emit("update:modelValue", strings);
    emit("removed", extractString(removed));
    showSnackbar(
      t("strings.removed-success", { name: extractString(removed) }),
      "success",
    );
  }
  pendingRemove.value = null;
  showConfirmRemove.value = false;
}

function cancelRemove() {
  pendingRemove.value = null;
  showConfirmRemove.value = false;
}

function requestClearAll() {
  showConfirmClear.value = true;
}

function confirmClearAll() {
  itemsObjects.value = [];
  emit("update:modelValue", []);
  showSnackbar(
    t("strings.cleared-all") ||
      t("strings.removed-success", { name: "" }) ||
      "All values cleared",
    "success",
  );
  showConfirmClear.value = false;
}

function cancelClearAll() {
  showConfirmClear.value = false;
}

const newItem = ref("");
const canAdd = computed(() => {
  const val = newItem.value && newItem.value.trim().length > 0;
  return val;
});

function onAdd() {
  const val = newItem.value && newItem.value.trim();
  if (!val) {
    showSnackbar(t("strings.invalid-value"));
    return;
  }

  // Check for duplicates
  const currentStrings = itemsObjects.value.map((o) => o.name);
  if (currentStrings.includes(val)) {
    showSnackbar(t("strings.duplicate"));
    return;
  }

  // Check max items
  if (props.maxItems != null && itemsObjects.value.length >= props.maxItems) {
    showSnackbar(t("strings.max-reached", { count: props.maxItems }));
    return;
  }

  itemsObjects.value.push(makeObj(val));
  const strings = itemsObjects.value.map((o) => o.name);
  emit("update:modelValue", strings);
  emit("added", val);
  newItem.value = "";
  showSnackbar(t("strings.added-success"), "success");
}
</script>

<style scoped></style>
